import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import {
  useParams,
  useSimpleError,
  useQueryWithSimpleErrorHandling,
  useInsuranceTypeConfiguration,
  useDictionaryHandling,
  useToast
} from 'hooks';
import { useTranslation } from 'react-i18next';
import { insuranceTypeNames } from 'constants/defaultValues';
import { INSURERS_QUERY, INSURED_PERIODS_QUERY } from 'features/insuranceDocument/edit/general/insuranceData/queries';
import { INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { CHECK_CASCO_INSURANCE_FLOW } from '../../../mutations';
import { CASCO_QUOTATIONS_RETRIEVED, CASCO_CHECK_FLOW_FAILED } from '../../../subscriptions';
import InsuranceFlowCascoChecksComponent from './InsuranceFlowCascoChecksComponent';
import { isValid, getErrors, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { shape, required, fromModel, errorMessage, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { onlyLetters, integer } from 'utils/simple-validations/validations';
import { validatePeriods, validateInsurers } from './validations';
import i18next from 'i18next';

const validatorConfigurationJson = (periodRange, insurersList) =>
  fromModel(_ =>
    shape({
      DocumentDate: required |> errorMessage(i18next.t('Validations.CheckCascoFlow.DocumentDateMandatory')),
      MonthNo:
        [
          required |> errorMessage(i18next.t('Validations.CheckCascoFlow.MonthNoMandatory')),
          integer |> errorMessage(i18next.t('Validations.CheckCascoFlow.MonthNoInteger'))
        ] |> stopOnFirstFailure,
      Periodicity:
        [
          required |> errorMessage(i18next.t('Validations.CheckCascoFlow.PeriodicityMandatory')),
          integer |> errorMessage(i18next.t('Validations.CheckCascoFlow.PeriodicityInteger'))
        ] |> stopOnFirstFailure,
      CurrencyCode:
        [
          required |> errorMessage(i18next.t('Validations.CheckCascoFlow.CurrencyCodeMandatory')),
          onlyLetters |> errorMessage(i18next.t('Validations.CheckCascoFlow.CurrencyCodeString'))
        ] |> stopOnFirstFailure,
      Asset: shape({
        ItemName: required |> errorMessage(i18next.t('Validations.CheckCascoFlow.ItemNameMandatory'))
      }),
      Periods: validatePeriods(periodRange.MinValue, periodRange.MaxValue) |> stopOnFirstFailure,
      Insurers: validateInsurers(insurersList) |> stopOnFirstFailure
    })
  );

const InsuranceFlowCascoChecksContainer = () => {
  const showError = useSimpleError();
  const { t } = useTranslation();
  const addToast = useToast();
  const [cascoQuotations, setCascoQuotations] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState({
    success: null,
    loading: false,
    message: null
  });
  const [openDialog, setDialog] = useState(false);
  const [periodRange, setPeriodRange] = useState({});
  const [configurationJSON, setConfigurationJSON] = useState(null);
  const [backupConfigJSON, setBackupConfigJSON] = useState(null); // contains the config at dialog open

  const { cascoInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(new Array(insuranceTypeNames.CASCO));
  const { getIdByCode: getInsuranceTypeIdByCode } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const cascoTypeCode = cascoInsuranceTypeConfiguration?.PropertyCode;
  const cascoTypeId = useMemo(() => getInsuranceTypeIdByCode(cascoTypeCode), [getInsuranceTypeIdByCode, cascoTypeCode]);
  const { data: insurersData } = useQueryWithSimpleErrorHandling(INSURERS_QUERY, {
    variables: { insuranceTypeId: cascoTypeId },
    skip: !cascoTypeId
  });

  useQueryWithSimpleErrorHandling(INSURED_PERIODS_QUERY, {
    variables: { insuranceTypeCode: cascoTypeCode },
    skip: !cascoTypeCode,
    onCompleted: data => {
      setPeriodRange({
        min: data?.periodRange.min,
        max: data?.periodRange.max
      });
    }
  });

  const configuration = useParams('CheckInsuranceFlowConfiguration');

  const validatorConfiguration = useMemo(
    () =>
      validatorConfigurationJson(
        cascoInsuranceTypeConfiguration?.InsurancePeriodRange,
        insurersData?.insurers.map(x => x.name)
      ),
    [cascoInsuranceTypeConfiguration?.InsurancePeriodRange, insurersData?.insurers]
  );

  const [validation, validate] = useDirtyFieldValidation(validatorConfiguration);

  const [checkCASCOInsuranceFlow] = useMutation(CHECK_CASCO_INSURANCE_FLOW, {
    onError: error => {
      showError(error);
      setVerificationStatus({ success: false, loading: false, message: error });
    }
  });

  const handleCheckCascoQuotations = () => {
    checkCASCOInsuranceFlow({
      variables: {
        input: {
          configuration: JSON.stringify(configurationJSON)
        }
      }
    });
    setVerificationStatus({ success: null, loading: true, message: null });
    setCascoQuotations(null);
  };

  useEffect(() => {
    if (!configuration) return;
    var config = JSON.parse(configuration);
    var cascoConfig = config.find(x => x.InsuranceTypeCode === cascoTypeCode);
    /* DocumentNumbers array was deleted because it should not be displayed in json editor. DocumentNumbers can only be edited in Lsng Admin.
       InsuranceTypeCode was deleted because it is not necesary to be displayed in json editor.*/
    delete cascoConfig?.DocumentNumbers;
    delete cascoConfig?.InsuranceTypeCode;
    setConfigurationJSON(cascoConfig);
  }, [cascoTypeCode, configuration]);

  const handleReloadJson = useCallback(() => {
    if (!configuration) return;
    var config = JSON.parse(configuration);
    var cascoConfig = config.find(x => x.InsuranceTypeCode === cascoTypeCode);
    delete cascoConfig.DocumentNumbers;
    delete cascoConfig.InsuranceTypeCode;
    setConfigurationJSON(cascoConfig);
  }, [cascoTypeCode, configuration]);

  const handleOpenDialog = useCallback(() => {
    setBackupConfigJSON(configurationJSON);
    setDialog(true);
  }, [configurationJSON]);

  const handleCancelDialog = useCallback(() => {
    setConfigurationJSON(backupConfigJSON);
    setDialog(false);
  }, [backupConfigJSON]);

  const handleConcatValidationErrors = useCallback(() => {
    let errorMessage = '';
    if (!isValid(validation.DocumentDate)) errorMessage += getErrors(validation.DocumentDate);
    if (!isValid(validation.MonthNo)) errorMessage += getErrors(validation.MonthNo);
    if (!isValid(validation.Periodicity)) errorMessage += getErrors(validation.Periodicity);
    if (!isValid(validation.CurrencyCode)) errorMessage += getErrors(validation.CurrencyCode);
    if (!isValid(validation.Periods)) errorMessage += getErrors(validation.Periods);
    if (!isValid(validation.Insurers)) errorMessage += getErrors(validation.Insurers);
    if (!isValid(validation.Asset.ItemName)) errorMessage += getErrors(validation.Asset.ItemName);
    return errorMessage;
  }, [validation]);

  const handleCloseDialog = useCallback(() => {
    if (!validate(configurationJSON)) {
      const errorMessages = handleConcatValidationErrors();
      if (errorMessages === '') return;
      addToast(errorMessages, 'error', { toastId: 'insuranceFlowErrorMessageToast' });
      setDialog(true);
      return;
    }
    setDialog(false);
  }, [addToast, configurationJSON, validate, handleConcatValidationErrors]);

  useEffect(() => {
    if (!configurationJSON || !insurersData || !cascoInsuranceTypeConfiguration) return;
    validate(configurationJSON);
  }, [validate, configurationJSON, cascoInsuranceTypeConfiguration, insurersData]);

  const handleChangeJsonValue = input => {
    setConfigurationJSON(input.updated_src);
  };

  useConditionalSubscription(CASCO_QUOTATIONS_RETRIEVED, {
    onData: ({ data: { data } }) => {
      if (
        data?.cascoQuotationsRetrieved?.cascoQuotationsLite?.length <= 0 ||
        data?.cascoQuotationsRetrieved?.cascoQuotationsLite === null
      ) {
        setCascoQuotations(data?.cascoQuotationsRetrieved?.cascoQuotationsLite);
        setVerificationStatus({
          success: true,
          loading: false,
          message: t('Settings.InsuranceIntegrationChecks.EmptyList')
        });
        return;
      }
      setCascoQuotations(data?.cascoQuotationsRetrieved?.cascoQuotationsLite);
      setVerificationStatus({ success: true, loading: false, message: null });
    }
  });

  useConditionalSubscription(CASCO_CHECK_FLOW_FAILED, {
    onData: ({ data: { data } }) => {
      setVerificationStatus({
        success: false,
        loading: false,
        message: data?.cascoCheckFlowFailed?.errorMessage
      });
    }
  });

  return (
    <InsuranceFlowCascoChecksComponent
      cascoQuotations={cascoQuotations}
      verificationStatus={verificationStatus}
      configurationJSON={configurationJSON}
      insurersData={insurersData}
      periods={periodRange}
      openDialog={openDialog}
      onOpenDialog={handleOpenDialog}
      onCloseDialog={handleCloseDialog}
      onCancelDialog={handleCancelDialog}
      onChangeJsonValue={handleChangeJsonValue}
      onCheckCascoQuotations={handleCheckCascoQuotations}
      onReloadJson={handleReloadJson}
    />
  );
};

InsuranceFlowCascoChecksContainer.propTypes = {};

export default InsuranceFlowCascoChecksContainer;
